import { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * useCharacterCampaign - Custom hook to look up and load campaign data for a character
 * @param {string} characterId - The character's ID
 * @param {object} user - The authenticated user
 * @returns {object} { characterCampaignId, isInCampaign, campaignCookbook }
 */
const useCharacterCampaign = (characterId, user) => {
  const [characterCampaignId, setCharacterCampaignId] = useState(null);
  const [isInCampaign, setIsInCampaign] = useState(false);
  const [campaignCookbook, setCampaignCookbook] = useState([]);

  // Look up which campaign this character belongs to
  useEffect(() => {
    const findCharacterCampaign = async () => {
      if (!characterId || !user) return;

      try {
        // Note: Firebase array-contains-any with objects doesn't work as expected.
        // We need to get all campaigns and filter client-side
        const allCampaignsQuery = query(collection(db, 'campaigns'));
        const campaignsSnapshot = await getDocs(allCampaignsQuery);

        let foundCampaignId = null;

        campaignsSnapshot.docs.forEach(docSnap => {
          const campaignData = docSnap.data();
          if (campaignData.players?.some(player => player.characterId === characterId)) {
            foundCampaignId = docSnap.id;
          }
        });

        setCharacterCampaignId(foundCampaignId);
        setIsInCampaign(!!foundCampaignId);

      } catch (error) {
        console.error('Error finding character campaign:', error);
        // Not being in a campaign is not an error - just disable collaborative features
        setIsInCampaign(false);
        setCharacterCampaignId(null);
      }
    };

    findCharacterCampaign();
  }, [characterId, user]);

  // Load campaign cookbook when campaign ID is available (real-time listener for collaborative updates)
  useEffect(() => {
    if (!characterCampaignId) {
      setCampaignCookbook([]);
      return;
    }

    // Set up real-time listener for campaign cookbook updates
    const campaignDocRef = doc(db, 'campaigns', characterCampaignId);
    const unsubscribe = onSnapshot(
      campaignDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const campaignData = docSnap.data();
          setCampaignCookbook(campaignData.cookbook || []);
        } else {
          setCampaignCookbook([]);
        }
      },
      (error) => {
        console.error('Error loading campaign cookbook:', error);
        setCampaignCookbook([]);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [characterCampaignId]);

  return {
    characterCampaignId,
    isInCampaign,
    campaignCookbook
  };
};

export default useCharacterCampaign;
